import type {
  DialogueRecord,
  EngagementRecord,
  IncidentRecord,
  IssuerRecord,
  MilestoneRecord,
  ObjectiveRecord,
  SecurityRecord,
  SourceRecord,
} from '@fruuit/shared'
import { useMemo } from 'react'
import type { FormValues } from './form'
import { todayIso } from './format'
import { RecordFormDialog } from './RecordFormDialog'
import {
  DIALOGUE_FIELDS,
  ENGAGEMENT_FIELDS,
  INCIDENT_FIELDS,
  INCIDENT_PROCEEDING_FIELDS,
  ISSUER_FIELDS,
  MILESTONE_FIELDS,
  OBJECTIVE_FIELDS,
  SECURITY_FIELDS,
  SOURCE_FIELDS,
} from './specs'

/**
 * Re-homed authoring forms (P-UI-001, R-09). Each dialog is self-contained: mount it with
 * `open`/`onClose`, pass the parent context, and receive the saved record in `onSaved`.
 * Lane D's case page mounts the engagement, objective, milestone and correspondence dialogs.
 */

interface BaseProps<T> {
  open: boolean
  onClose: () => void
  onSaved?: (record: T) => void
}

export interface CreateIssuerDialogProps extends BaseProps<IssuerRecord> {
  defaults?: FormValues
}

export function CreateIssuerDialog({ open, onClose, onSaved, defaults }: CreateIssuerDialogProps) {
  return (
    <RecordFormDialog
      kind="issuers"
      open={open}
      onClose={onClose}
      onSaved={onSaved}
      title="Add issuer"
      description="Add a company to the canonical issuer master. Coverage and mapping follow from holdings."
      fields={ISSUER_FIELDS}
      defaults={defaults}
      submitLabel="Add issuer"
      successMessage="Issuer added to the master. No client view changed."
    />
  )
}

export interface CreateSecurityDialogProps extends BaseProps<SecurityRecord> {
  issuerId?: string
  defaults?: FormValues
}

export function CreateSecurityDialog({
  open,
  onClose,
  onSaved,
  issuerId,
  defaults,
}: CreateSecurityDialogProps) {
  const initial = useMemo(
    () => ({
      identifier_type: 'ISIN',
      mapping_state: issuerId ? 'verified' : 'unresolved',
      mapping_source: 'Manual entry by Fruuit staff',
      effective_from: todayIso(),
      ...(issuerId ? { issuer_id: issuerId } : {}),
      ...defaults,
    }),
    [issuerId, defaults],
  )
  return (
    <RecordFormDialog
      kind="securities"
      open={open}
      onClose={onClose}
      onSaved={onSaved}
      title="Add security"
      description="Record a security identifier and how it maps to an issuer."
      fields={SECURITY_FIELDS}
      defaults={initial}
      submitLabel="Add security"
      successMessage="Security added to the master. No client view changed."
    />
  )
}

export interface EditEngagementDialogProps extends BaseProps<EngagementRecord> {
  /** Edit this engagement; omit to create a new one. */
  engagement?: EngagementRecord | null
  defaults?: FormValues
}

export function EditEngagementDialog({
  open,
  onClose,
  onSaved,
  engagement,
  defaults,
}: EditEngagementDialogProps) {
  const initial = useMemo(() => ({ status: 'active', ...defaults }), [defaults])
  return (
    <RecordFormDialog
      kind="engagements"
      open={open}
      onClose={onClose}
      onSaved={onSaved}
      record={engagement ?? null}
      title={engagement ? 'Edit engagement' : 'Open engagement'}
      description={
        engagement
          ? 'Changes stay internal until a reviewer releases them to subscribing clients.'
          : 'Open a case against a published assessment. Clients see it only after release.'
      }
      fields={ENGAGEMENT_FIELDS}
      defaults={initial}
      locked={['issuer_id', 'client_org_id']}
      submitLabel={engagement ? 'Save changes' : 'Open engagement'}
      successMessage={
        engagement
          ? 'Engagement saved. Clients keep seeing the released version until it is released again.'
          : 'Engagement opened. Nothing is visible to clients until release.'
      }
    />
  )
}

export interface CreateObjectiveDialogProps extends BaseProps<ObjectiveRecord> {
  engagementId: string
  /** Edit this objective instead of creating one. */
  objective?: ObjectiveRecord | null
}

export function CreateObjectiveDialog({
  open,
  onClose,
  onSaved,
  engagementId,
  objective,
}: CreateObjectiveDialogProps) {
  const fixed = useMemo(() => ({ engagement_id: engagementId }), [engagementId])
  return (
    <RecordFormDialog
      kind="objectives"
      open={open}
      onClose={onClose}
      onSaved={onSaved}
      record={objective ?? null}
      title={objective ? 'Edit objective' : 'Add objective'}
      description="State the baseline and the change Fruuit seeks. Released objectives are what clients see."
      fields={OBJECTIVE_FIELDS}
      fixed={fixed}
      submitLabel={objective ? 'Save changes' : 'Add objective'}
      successMessage={
        objective
          ? 'Objective saved. Clients see the change once a reviewer releases it.'
          : 'Objective added. Clients see it once a reviewer releases it.'
      }
    />
  )
}

export interface CreateMilestoneDialogProps extends BaseProps<MilestoneRecord> {
  objectiveId: string
  milestone?: MilestoneRecord | null
}

export function CreateMilestoneDialog({
  open,
  onClose,
  onSaved,
  objectiveId,
  milestone,
}: CreateMilestoneDialogProps) {
  const fixed = useMemo(() => ({ objective_id: objectiveId }), [objectiveId])
  const defaults = useMemo(() => ({ assessment_date: todayIso() }), [])
  return (
    <RecordFormDialog
      kind="milestones"
      open={open}
      onClose={onClose}
      onSaved={onSaved}
      record={milestone ?? null}
      title={milestone ? 'Edit evidence review' : 'Record evidence review'}
      description="An evidence review records the stage evidenced for this objective. Only published evidence reviews move the stage."
      fields={MILESTONE_FIELDS}
      fixed={fixed}
      defaults={defaults}
      submitLabel="Save draft"
      successMessage="Draft saved. Submit it for review when it is ready; the stage changes only on publication."
    />
  )
}

export interface CreateDialogueDialogProps extends BaseProps<DialogueRecord> {
  engagementId: string
  dialogue?: DialogueRecord | null
}

export function CreateDialogueDialog({
  open,
  onClose,
  onSaved,
  engagementId,
  dialogue,
}: CreateDialogueDialogProps) {
  const fixed = useMemo(() => ({ engagement_id: engagementId }), [engagementId])
  const defaults = useMemo(
    () => ({ event_type: 'written_response', event_date: todayIso(), substantive: false }),
    [],
  )
  return (
    <RecordFormDialog
      kind="dialogues"
      open={open}
      onClose={onClose}
      onSaved={onSaved}
      record={dialogue ?? null}
      title={dialogue ? 'Edit correspondence' : 'Record correspondence'}
      description="Company responses do not replace Fruuit's judgment. Correspondence reaches clients only after review."
      fields={DIALOGUE_FIELDS}
      fixed={fixed}
      defaults={defaults}
      submitLabel="Save draft"
      successMessage="Draft saved. It is not visible to clients until an independent reviewer publishes it."
    />
  )
}

export interface CreateSourceDialogProps extends BaseProps<SourceRecord> {
  issuerId?: string | null
  clientOrgId?: string | null
}

export function CreateSourceDialog({
  open,
  onClose,
  onSaved,
  issuerId,
  clientOrgId,
}: CreateSourceDialogProps) {
  const defaults = useMemo(
    () => ({
      sharing: 'internal',
      redistribution: 'internal_only',
      retrieved_at: todayIso(),
      ...(issuerId ? { issuer_id: issuerId } : {}),
      ...(clientOrgId ? { client_org_id: clientOrgId } : {}),
    }),
    [issuerId, clientOrgId],
  )
  return (
    <RecordFormDialog
      kind="sources"
      open={open}
      onClose={onClose}
      onSaved={onSaved}
      title="Add evidence source"
      description="Record provenance and sharing rights. Store references and summaries, never full-text articles."
      intro="Do not paste private client data or unlicensed material. A source cited by published work becomes immutable."
      fields={SOURCE_FIELDS}
      defaults={defaults}
      submitLabel="Add source"
      successMessage="Source added. Clients see it only if it is shareable and cited by published work."
    />
  )
}

export interface CreateIncidentDialogProps extends BaseProps<IncidentRecord> {
  issuerId?: string | null
  clientOrgId?: string | null
}

const INCIDENT_ALL_FIELDS = [...INCIDENT_FIELDS, ...INCIDENT_PROCEEDING_FIELDS]

export function CreateIncidentDialog({
  open,
  onClose,
  onSaved,
  issuerId,
  clientOrgId,
}: CreateIncidentDialogProps) {
  const defaults = useMemo(
    () => ({
      ...(issuerId ? { issuer_id: issuerId } : {}),
      ...(clientOrgId ? { client_org_id: clientOrgId } : {}),
    }),
    [issuerId, clientOrgId],
  )
  return (
    <RecordFormDialog
      kind="incidents"
      open={open}
      onClose={onClose}
      onSaved={onSaved}
      title="Record incident"
      description="Incidents keep a dated legal history. A status change is a new record linked to the previous one."
      intro="For a dated legal proceeding, fill in event identity, authority, proceeding reference, legal entity and status-as-of together."
      fields={INCIDENT_ALL_FIELDS}
      defaults={defaults}
      submitLabel="Record incident"
      successMessage="Incident recorded. Clients see it only when a published assessment cites it."
    />
  )
}
